import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLang } from '@/i18n/LanguageContext';
import { API } from '@/lib/config';
import { Container, SectionHeading, SectionKicker, TricolourRibbon } from '@/components/common/Layout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2 } from 'lucide-react';

const ROLES = [
  'member',
  'volunteer',
  'policy_expert',
  'future_candidate',
  'upsc_mpsc_aspirant',
];

export default function JoinPage() {
  const { t } = useLang();
  const [form, setForm] = useState({
    full_name: '', mobile: '', email: '', state: '', district: '',
    profession: '', area_of_interest: '', role_type: 'member',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSuccess(null);
    try {
      const res = await axios.post(`${API}/members`, form);
      const msg = res.data.message || t('join.success');
      toast.success(msg);
      setSuccess({ id: res.data.id, message: msg });
      setForm({
        full_name: '', mobile: '', email: '', state: '', district: '',
        profession: '', area_of_interest: '', role_type: 'member',
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.slice(-1)[0] || 'field'}: ${d.msg}`).join(', ')
        : (typeof detail === 'string' ? detail : 'Please check your inputs and try again.');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <section className="hero-surface">
        <Container className="py-14 sm:py-16">
          <SectionKicker>{t('nav.join')}</SectionKicker>
          <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B1220]">
            {t('join.title')}
          </h1>
          <p className="mt-4 max-w-3xl text-[#5B6B82] leading-relaxed">{t('join.lede')}</p>
        </Container>
      </section>

      <section className="py-14">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <form onSubmit={onSubmit} data-testid="join-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField id="jn-name" label={t('join.form.full_name')} required>
                  <Input id="jn-name" value={form.full_name} onChange={update('full_name')} required minLength={2} data-testid="join-full-name" />
                </FormField>
                <FormField id="jn-mobile" label={t('join.form.mobile')} required>
                  <Input id="jn-mobile" value={form.mobile} onChange={update('mobile')} required data-testid="join-mobile" />
                </FormField>
                <FormField id="jn-email" label={t('join.form.email')} required>
                  <Input id="jn-email" type="email" value={form.email} onChange={update('email')} required data-testid="join-email" />
                </FormField>
                <FormField id="jn-state" label={t('join.form.state')} required>
                  <Input id="jn-state" value={form.state} onChange={update('state')} required data-testid="join-state" />
                </FormField>
                <FormField id="jn-district" label={t('join.form.district')} required>
                  <Input id="jn-district" value={form.district} onChange={update('district')} required data-testid="join-district" />
                </FormField>
                <FormField id="jn-prof" label={t('join.form.profession')} required>
                  <Input id="jn-prof" value={form.profession} onChange={update('profession')} required data-testid="join-profession" />
                </FormField>
                <div className="md:col-span-2">
                  <FormField id="jn-area" label={t('join.form.area_of_interest')} required>
                    <Input id="jn-area" value={form.area_of_interest} onChange={update('area_of_interest')} required data-testid="join-area" />
                  </FormField>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm font-medium text-[#0B1220]">{t('join.form.role')} <span className="text-[#B42318]">*</span></Label>
                  <RadioGroup
                    value={form.role_type}
                    onValueChange={(v) => setForm((s) => ({ ...s, role_type: v }))}
                    className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3"
                    data-testid="join-role-group"
                  >
                    {ROLES.map((r) => (
                      <label
                        key={r}
                        htmlFor={`role-${r}`}
                        className={[
                          'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                          form.role_type === r
                            ? 'border-[#062B5C] bg-[#F5F8FC]'
                            : 'border-[#D7E1EF] bg-white hover:bg-[#F5F8FC]',
                        ].join(' ')}
                      >
                        <RadioGroupItem value={r} id={`role-${r}`} data-testid={`join-role-${r}`} />
                        <span className="text-sm text-[#0B1220]">{t(`join.roles.${r}`)}</span>
                      </label>
                    ))}
                  </RadioGroup>
                </div>
                <div className="md:col-span-2">
                  <Button type="submit" disabled={submitting} className="bg-[#062B5C] hover:bg-[#05244d] text-white" data-testid="join-form-submit-button">
                    {submitting ? t('common.submitting') : t('common.join_cta')}
                  </Button>
                </div>
              </form>

              {success && (
                <Card className="mt-6 border-[#138A4B]/40 bg-[#EAF6EF]" data-testid="join-success">
                  <CardContent className="p-5 flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#138A4B]" />
                    <div>
                      <div className="font-semibold text-[#0B1220]">{t('common.thank_you')}</div>
                      <div className="text-sm text-[#0B1220]">{success.message}</div>
                      <div className="text-xs text-[#5B6B82] mt-1">Reference: {success.id}</div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <aside className="lg:col-span-4 space-y-5">
              <Card className="border-[#D7E1EF] bg-white">
                <CardContent className="p-5">
                  <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Why join</div>
                  <ul className="mt-3 space-y-2 text-sm text-[#0B1220]">
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#138A4B] mt-0.5" />Merit-based leadership</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#138A4B] mt-0.5" />Transparent internal processes</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#138A4B] mt-0.5" />Governance-first policy platform</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#138A4B] mt-0.5" />Free membership for every citizen</li>
                  </ul>
                </CardContent>
              </Card>
            </aside>
          </div>
        </Container>
      </section>
      <TricolourRibbon />
    </div>
  );
}

function FormField({ id, label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-sm font-medium text-[#0B1220]">
        {label} {required && <span className="text-[#B42318]">*</span>}
      </Label>
      {children}
    </div>
  );
}
